// Ticket de venta: datos para imprimirlo (58/80 mm) y texto para compartir por WhatsApp.
import { PAYMENT_METHODS, balanceLabel, formatAdjust, formatQty, whatsappNumber } from '@pos/core';
import type { BusinessRow, SaleRow } from '@pos/local-db';

export interface TicketData {
  business: Pick<BusinessRow, 'name' | 'address' | 'contact_phone' | 'cuit'>;
  sale: SaleRow;
  sellerName: string | null;
  footer: string;
  money: (cents: number) => string;
  /** Para "Devolución de la venta N". */
  originalNumber?: string | null;
  customerName?: string | null;
  /** Saldo de la cuenta corriente del cliente después de la venta (null: no se muestra). */
  customerBalance?: number | null;
  /** URL del logo para imprimir arriba (null: sin logo o desactivado en la configuración). */
  logoUrl?: string | null;
}

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

export function ticketTitle(sale: SaleRow): string {
  return sale.kind === 'return' ? `Devolución ${sale.number}` : `Venta ${sale.number}`;
}

/** Cantidad × precio de una línea, p. ej. "2 x $ 1.500" o "0,75 kg x $ 2.800". */
export function lineDetail(item: SaleRow['items'][number], money: (c: number) => string): string {
  return `${formatQty(item.qty_milli, item.unit_type)} x ${money(item.unit_price)}`;
}

/** Ajuste por medio de pago en %, calculado a partir de los importes (para mostrarlo en el ticket). */
function adjustLabel(base: number, adjustment: number): string {
  if (base === 0 || adjustment === 0) return '';
  return formatAdjust(Math.round((adjustment / base) * 10000));
}

/** Texto plano del ticket (WhatsApp). */
export function ticketText(data: TicketData): string {
  const { business, sale, money } = data;
  const lines: string[] = [];
  lines.push(`*${business.name}*`);
  if (business.address) lines.push(business.address);
  lines.push(`${ticketTitle(sale)} · ${dateFormat.format(new Date(sale.occurred_at))}`);
  if (data.originalNumber) lines.push(`De la venta ${data.originalNumber}`);
  if (data.customerName) lines.push(`Cliente: ${data.customerName}`);
  lines.push('');
  for (const item of sale.items) {
    lines.push(`${item.description}`);
    lines.push(`  ${lineDetail(item, money)} = ${money(item.total + item.discount)}${item.discount ? ` (desc. ${money(item.discount)})` : ''}`);
  }
  lines.push('');
  if (sale.discount_total > 0) {
    lines.push(`Subtotal: ${money(sale.subtotal)}`);
    lines.push(`Descuentos: −${money(sale.discount_total)}`);
  }
  for (const p of sale.payments) {
    const adj = adjustLabel(p.base, p.adjustment);
    lines.push(`${PAYMENT_METHODS[p.method].label}${adj ? ` (${adj})` : ''}: ${money(p.amount)}`);
    if (p.method === 'cash' && p.tendered != null && p.tendered > p.amount) {
      lines.push(`  Pagó con ${money(p.tendered)} · Vuelto ${money(p.tendered - p.amount)}`);
    }
  }
  lines.push(`*${sale.kind === 'return' ? 'Total devuelto' : 'Total'}: ${money(sale.total)}*`);
  if (data.customerBalance != null) lines.push(`Saldo en cuenta: ${balanceLabel(data.customerBalance, money)}`);
  lines.push('');
  if (data.footer.trim()) lines.push(data.footer.trim());
  lines.push('Comprobante no válido como factura.');
  return lines.join('\n');
}

/** Link para compartir por WhatsApp (sin número: la persona elige el contacto). */
export function whatsappLink(text: string, phone?: string | null): string {
  const digits = whatsappNumber(phone) ?? '';
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/** HTML del ticket para imprimir (impresora térmica de 58 u 80 mm, o cualquier impresora). */
export function ticketHtml(data: TicketData, width: 58 | 80): string {
  const { business, sale, money } = data;
  const row = (left: string, right: string, strong = false) =>
    `<div class="t-row${strong ? ' t-strong' : ''}"><span>${escapeHtml(left)}</span><span>${escapeHtml(right)}</span></div>`;
  const parts: string[] = [];
  if (data.logoUrl) parts.push(`<img class="t-logo" src="${escapeHtml(data.logoUrl)}" alt="">`);
  parts.push(`<div class="t-center t-strong t-big">${escapeHtml(business.name)}</div>`);
  if (business.address) parts.push(`<div class="t-center">${escapeHtml(business.address)}</div>`);
  if (business.contact_phone) parts.push(`<div class="t-center">Tel. ${escapeHtml(business.contact_phone)}</div>`);
  if (business.cuit) parts.push(`<div class="t-center">CUIT ${escapeHtml(business.cuit)}</div>`);
  parts.push('<hr>');
  parts.push(row(ticketTitle(sale), dateFormat.format(new Date(sale.occurred_at))));
  if (data.originalNumber) parts.push(`<div>De la venta ${escapeHtml(data.originalNumber)}</div>`);
  if (data.sellerName) parts.push(`<div>Atendió: ${escapeHtml(data.sellerName)}</div>`);
  if (data.customerName) parts.push(`<div>Cliente: ${escapeHtml(data.customerName)}</div>`);
  parts.push('<hr>');
  for (const item of sale.items) {
    parts.push(`<div>${escapeHtml(item.description)}</div>`);
    parts.push(row(`  ${lineDetail(item, money)}`, money(item.total + item.discount)));
    if (item.discount) parts.push(row('  Descuento', `−${money(item.discount)}`));
  }
  parts.push('<hr>');
  if (sale.discount_total > 0) parts.push(row('Subtotal', money(sale.subtotal)), row('Descuentos', `−${money(sale.discount_total)}`));
  for (const p of sale.payments) {
    const adj = adjustLabel(p.base, p.adjustment);
    if (p.adjustment) parts.push(row(`${p.adjustment < 0 ? 'Desc.' : 'Recargo'} ${PAYMENT_METHODS[p.method].label} ${adj}`, money(p.adjustment)));
    if (p.rounding) parts.push(row('Redondeo', money(p.rounding)));
  }
  parts.push(row(sale.kind === 'return' ? 'TOTAL DEVUELTO' : 'TOTAL', money(sale.total), true));
  for (const p of sale.payments) {
    parts.push(row(PAYMENT_METHODS[p.method].label, money(p.amount)));
    if (p.method === 'cash' && p.tendered != null && p.tendered > p.amount) {
      parts.push(row('  Pagó con', money(p.tendered)), row('  Vuelto', money(p.tendered - p.amount)));
    }
  }
  if (data.customerBalance != null) parts.push('<hr>', row('Saldo en cuenta', balanceLabel(data.customerBalance, money), true));
  parts.push('<hr>');
  if (data.footer.trim()) parts.push(`<div class="t-center">${escapeHtml(data.footer.trim())}</div>`);
  parts.push('<div class="t-center t-small">Comprobante no válido como factura</div>');
  return `<div class="t-ticket" style="width:${width === 58 ? '48mm' : '72mm'}">${parts.join('')}</div>`;
}

/** Imprime el ticket con el diálogo de impresión del navegador (solo se imprime el ticket). */
export function printTicket(data: TicketData, width: 58 | 80): void {
  printHtml(ticketHtml(data, width));
}

/** Imprime un comprobante (HTML con las clases t-*) sin el resto de la pantalla. */
export function printHtml(html: string): void {
  document.getElementById('print-ticket')?.remove();
  const container = document.createElement('div');
  container.id = 'print-ticket';
  container.innerHTML = html;
  document.body.appendChild(container);
  const cleanup = () => {
    container.remove();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  // El logo tiene que estar cargado antes de imprimir. Si no carga (sin conexión y nunca se vio
  // en este dispositivo), se imprime sin él; como mucho se espera 3 segundos.
  const images = [...container.querySelectorAll('img')].filter((img) => !img.complete);
  if (images.length === 0) return window.print();
  const loaded = Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener(
            'error',
            () => {
              img.remove();
              resolve();
            },
            { once: true },
          );
        }),
    ),
  );
  void Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, 3000))]).then(() => {
    for (const img of container.querySelectorAll('img')) if (!img.complete || img.naturalWidth === 0) img.remove();
    window.print();
  });
}
